'use strict';

import * as util from '../util/Common.js';
import {DataTypes} from '../viewer/DomItem.js';
import {JsonLdUtil} from '../viewer/JsonLdManifestLoader.js';

const privateMap = util.createPrivateMapper();

export const IIIFv2Util = {

  getLink: function(vs) {
    const ret = [];
    const l = vs.length;
    for (let i = 0; i < l; i++) {
      const v = vs[i];
      let url;
      let label;
      if (typeof(v) == 'string') {
        url = v;
        label = v;
      } else if (v['@id']) {
        url = v['@id'];
        label = v['label'] ? v['label'] : url;
      }
      if (!url) {
        continue;
      }
      ret[ret.length] = {
        type: DataTypes.Link,
        label: label,
        format: v['format'],
        url: url
      };
    }
    if (!ret.length) {
      return null;
    }
    return ret;
  },

  getImage: function(vs) {
    const ret = [];
    const l = vs.length;
    for (let i = 0; i < l; i++) {
      const v = vs[i];
      if (typeof(v) == 'string') {
        ret[ret.length] = {
          type: DataTypes.Image,
          src: v
        };
      }
    }
    if (!ret.length) {
      return null;
    }
    return ret;
  },

  getText: function(vs) {
    const l = vs.length;
    const langs = {};
    let hasValue = false;
    for (let i = 0; i < l; i++) {
      const v = vs[i];
      let ln;
      let lv;
      if (typeof(v) == 'string') {
        ln = '@none';
        lv = v;
      } else if (typeof(v.value) == 'string') {
        ln = v.language ? v.language : '@none';
        lv = v.value;
      } else if (typeof(v['@value']) == 'string') {
        ln = v['@language'] ? v['@language'] : '@none';
        lv = v['@value'];
      }
      if (!ln) {
        continue;
      }
      // if (ln != '@none') {
      //   let langMap = base.langMap;
      //   if (!langMap) {
      //     langMap = {};
      //     base.langMap = langMap;
      //     base.langList = [];
      //   }
      //   if (!langMap[ln]) {
      //     langMap[ln] = true;
      //     base.langList[base.langList.length] = ln;
      //   }
      // }
      hasValue = true;
      let lvs = langs[ln];
      if (!lvs) {
        lvs = [];
        langs[ln] = lvs;
      }
      lvs[lvs.length] = lv;
    }
    if (!hasValue) {
      return null;
    }
    if (!langs['@none']) {
      for (let lk in langs) {
        // get first language value as default
        langs['@none'] = langs[lk];
        break;
      }
    }
    return {
      type: DataTypes.Text,
      text: langs
    };
  },

  getData: function(vs, valueType) {
    if (!vs || !vs.length) {
      return;
    }
    let data;
    switch (valueType) {
    case DataTypes.Text:
      data = this.getText(vs);
      break;
    case DataTypes.Image:
      data = this.getImage(vs);
      break;
    case DataTypes.Link:
      data = this.getLink(vs);
      break;
    }
    return data;
  },

  analyzeOn(target, on) {
    const fragIdx = on.indexOf('#');
    let frag;
    if (fragIdx >= 0) {
      frag = on.substring(fragIdx + 1);
    }
    if (frag) {
      if (frag.indexOf('xywh=') === 0) {
        target['xywh'] = JsonLdUtil.convertXywh(frag.substring('xywh='.length));
      }
    }
  },

  createImage(json) {
    const resource = json.resource;
    if (!resource) {
      return null;
    }
    if (resource['@type'] == 'oa:Choice') {
      resource = resource['default'];
    }
    if (!resource) {
      return null;
    }
    const imageResource = this.createImageResource(resource);
    if (json.on) {
      this.analyzeOn(imageResource, json.on)
    }
    return imageResource;
  },

  createImageResource(json) {
    if (!json) {
      return null;
    }
    if (json.service && json.service['@id']) {
      return {
        source: {
          id: json.service['@id']
        },
        type: 'iiifimage'
      };
    } else if (json['@id']) {
      return {
        source: {
          id: json['@id']
        },
        type: 'image'
      };
    }
    return null;
  },

  createThumbnail: function(json){
    if (!json) {
      return;
    }
    if (typeof(json) == 'string') {
      return {
        source: {
          id: json
        },
        type: 'image'
      };
    } else if (json['@id']) {
      return {
        source: {
          id: json['@id']
        },
        type: 'image'
      };
    }
    return null;
  },

  extractAttributes: function(node, json){
    const f = (fname, valueType) => {
      const data = this.getData(JsonLdUtil.getLdField(json, fname), valueType);
      if (!data) {
        return;
      }
      node[fname] = data;
    }
    f('label', DataTypes.Text);
    f('description', DataTypes.Text);
    f('attribution', DataTypes.Text);
    f('license', DataTypes.Text);
    f('logo', DataTypes.Image);

    node.metadata = [];
    if (json.metadata) {
      const l = json.metadata.length;
      for (let i = 0; i < l; i++) {
        const m = json.metadata[i];
        const data = this.getData(JsonLdUtil.getLdField(m, 'value'), DataTypes.Text);
        if (!data) {
          continue;
        }
        const label = this.getData(JsonLdUtil.getLdField(m, 'label'), DataTypes.Text);
        node.metadata[node.metadata.length] = {
          label: label,
          value: data
        };
      }
    }
    f('collections', DataTypes.Link);
    f('related', DataTypes.Link);
    f('seeAlso', DataTypes.Link);
    f('rendering', DataTypes.Link);

    if (json.thumbnail) {
      node.thumbnail = this.createThumbnail(json.thumbnail);
    }
  }

}

export class IIIFv2Analyzer {
  constructor(options){
    const pv = privateMap(this);
    pv.targetContexts = {
      'http://iiif.io/api/image/2/context.json': true, // This is for some site setting wrong context
      'http://iiif.io/api/presentation/2/context.json': true,
      'http://www.shared-canvas.org/ns/context.json': true
    };
  }

  isTarget(json){
    const pv = privateMap(this);
    const contexts = JsonLdUtil.getLdField(json, '@context');
    return JsonLdUtil.checkContext(contexts, pv.targetContexts);
  }

  analyzeAnnotationList(json, res) {
    const resources = json.resources;
    if (!resources || !resources.length) {
      return;
    }
    const targetResources = [];

    util.each(resources, (res) => {
      const on = res['on'];
      const obj = {
        on: on,
        resource: res['resource']
      };
      IIIFv2Util.analyzeOn(obj, on)
      targetResources[targetResources.length] = obj;
    });
    res.addRegions(targetResources);
  }

  createScene(json) {
    const scene = {};
    const source = {};
    scene.source = source;
    scene.id = json['@id'];
    scene.viewingHint = json.viewingHint;

    source.width = json.width;
    source.height = json.height;
    source.items = [];
    util.each(json.images, (imageJson) => {
      const image = IIIFv2Util.createImage(imageJson);
      if (!image) {
        return;
      }
      source.items.push(image);
    });
    if (source.items.length == 1) {
      // if single image
      const singleItem = source.items[0]
      if (!singleItem.xywh) {
        // if location not defined use canvas size to fit
        singleItem.xywh = {x: 0, y: 0, width: source.width, height: source.height}
        // singleItem.xywh = {x: source.width * 0.8, y: source.height * 0.2, width: source.width * 0.1, height: source.height * 0.6}
      }
    }

    source.annotationLists = [];
    const otherContent = JsonLdUtil.getLdField(json, 'otherContent');
    if (otherContent && otherContent.length) {
      util.each(otherContent, (oc) => {
        const iiifType = otherContent['@type'];
        const id = oc['@id'];
        if (!id) {
          return;
        }
        let type;
        if (iiifType != 'sc:AnnotationList') {
          type = 'iiifv2';
        }
        if (!type) {
          return;
        }
        source.annotationLists.push({
          id: id,
          type: type
        })
      });
    }

    IIIFv2Util.extractAttributes(scene, json);

    return scene;
  }

  createSequence(json) {
    const sequence = {
      viewTypes: ['default'],
      scenes: []
    };
    sequence.id = json['@id'];
    sequence.viewingHint = json.viewingHint
    sequence.viewingDirection = json.viewingDirection
    if (sequence.viewingDirection == 'right-to-left') {
      sequence.reverseOrder = true
    }

    if (json.viewingHint == 'paged') {
      sequence.viewTypes = ['paged', 'default']
    } else {
      sequence.viewTypes = ['default']
    }

    IIIFv2Util.extractAttributes(sequence, json);

    // sequence.label = json.label;
    util.each(json.canvases, (canvasJson) => {
      const scene = this.createScene(canvasJson);
      if (!scene) {
        return;
      }
      sequence.scenes.push(scene);
    });
    return sequence;
  }

  rebuildStructureTree(parent, structures, structMap){
    util.each(parent.ranges, (r) => {
      const struct = structMap[r];
      if (!struct) {
        return;
      }
      structures.push(struct);
      struct.parentId = parent.id;
      this.rebuildStructureTree(struct, structures, structMap);
    });
  }

  createStructure(json) {
    const structure = {
      parentId: json['within'],
      id: json['@id'],
      ranges: json['ranges'],
      data: {}
    };
    IIIFv2Util.extractAttributes(structure.data, json);
    if (json.canvases) {
      structure.data.sceneIds = [...json.canvases];
    }
    return structure;
  }

  analyzeManifest(json, manifest) {
    IIIFv2Util.extractAttributes(manifest.metadata, json);
    util.each(json['sequences'], (seqJson) => {
      const seq = this.createSequence(seqJson);
      if (!seq) {
        return;
      }
      manifest.sequences.push(seq);
    });
    let topStructs = [];
    const structMap = {};
    util.each(json['structures'], (structJson) => {
      
      const struct = this.createStructure(structJson);
      if (!struct) {
        return;
      }
      if (structJson.viewingHint == 'top') {
        topStructs.push(struct);
      }
      structMap[struct.id] = struct;
    });

    if (topStructs.length == 0) {
      // top structure not defined by viewinghint
      for (let stKey in structMap) {
        const struct = structMap[stKey];
        if (!struct) {
          continue;
        }
        if (!struct.parentId) {
          topStructs.push(struct);
        }
      }
    }
    if (topStructs.length == 1) {
      // single top structure
      util.each(topStructs[0].ranges, (r) => {
        const struct = structMap[r];
        if (!struct) {
          return;
        }
        struct.parentId = null;
        manifest.structures.push(struct);
        this.rebuildStructureTree(struct, manifest.structures, structMap);
      });
    } else {
      // multiple top structure
      util.each(topStructs, (topStruct) => {
        manifest.structures.push(topStruct);
        this.rebuildStructureTree(topStruct, manifest.structures, structMap);
      });
    }
  }

  analyze(json, manifest) {
    if (json['@type'] == 'sc:Manifest') {
      // Manifest
      this.analyzeManifest(json, manifest);
    } else if (json['@type'] == 'sc:Collection') {
      this.analyzeManifest(json, manifest);
    }
  }
}
